import React, { useCallback, useEffect, useState } from 'react'
import { useAuthContext } from '../hooks/useAuthContext'
import { getBackendUrl } from '../env'
import type { Program } from '../types'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

const emptyForm = {
  name: '',
  temperature: '',
  humidity: '',
  ec: '',
  ph: '',
  lights_on: '',
}

type FormState = typeof emptyForm

function authHeaders(token: string) {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  } as const
}

const AdminPrograms = () => {
  const { user } = useAuthContext()
  const [programs, setPrograms] = useState<Program[]>([])
  const [form, setForm] = useState<FormState>(emptyForm)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null)

  const loadPrograms = useCallback(async () => {
    const res = await fetch(`${getBackendUrl()}/api/programs/`)
    const json = await res.json()
    if (res.ok && Array.isArray(json)) {
      setPrograms(json)
    }
  }, [])

  useEffect(() => {
    void loadPrograms()
  }, [loadPrograms])

  const show = (type: 'ok' | 'err', text: string) => {
    setMessage({ type, text })
    window.setTimeout(() => setMessage(null), 6000)
  }

  const parseBody = () => {
    const name = form.name.trim()
    const temperatureRaw = form.temperature.trim()
    const humidityRaw = form.humidity.trim()
    const ecRaw = form.ec.trim()
    const phRaw = form.ph.trim()
    const lightsOnRaw = form.lights_on.trim()
    if (!name) {
      show('err', 'Name is required.')
      return null
    }
    if ([temperatureRaw, humidityRaw, ecRaw, phRaw, lightsOnRaw].some((value) => value === '')) {
      show('err', 'All numeric fields are required.')
      return null
    }
    const temperature = Number(temperatureRaw)
    const humidity = Number(humidityRaw)
    const ec = Number(ecRaw)
    const ph = Number(phRaw)
    const lights_on = Number(lightsOnRaw)
    if ([temperature, humidity, ec, ph, lights_on].some((n) => Number.isNaN(n))) {
      show('err', 'All numeric fields must be valid numbers.')
      return null
    }
    return { name, temperature, humidity, ec, ph, lights_on }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user?.token) return
    const body = parseBody()
    if (!body) return

    setLoading(true)
    try {
      if (editingId) {
        const res = await fetch(`${getBackendUrl()}/api/programs/${editingId}`, {
          method: 'PATCH',
          headers: authHeaders(user.token),
          body: JSON.stringify(body),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          show('err', typeof json.error === 'string' ? json.error : 'Update failed.')
        } else {
          show('ok', 'Program updated.')
          setForm(emptyForm)
          setEditingId(null)
          void loadPrograms()
        }
      } else {
        const res = await fetch(`${getBackendUrl()}/api/programs/`, {
          method: 'POST',
          headers: authHeaders(user.token),
          body: JSON.stringify(body),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          show('err', typeof json.error === 'string' ? json.error : 'Create failed.')
        } else {
          show('ok', 'Program created.')
          setForm(emptyForm)
          void loadPrograms()
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const startEdit = (p: Program) => {
    setEditingId(p._id)
    setForm({
      name: p.name,
      temperature: String(p.temperature),
      humidity: String(p.humidity),
      ec: String(p.ec),
      ph: String(p.ph),
      lights_on: String(p.lights_on),
    })
  }

  const cancelEdit = () => {
    setEditingId(null)
    setForm(emptyForm)
  }

  const handleDelete = async (p: Program) => {
    if (!user?.token) return
    if (!window.confirm(`Delete program “${p.name}”?`)) return
    setLoading(true)
    try {
      const res = await fetch(`${getBackendUrl()}/api/programs/${p._id}`, {
        method: 'DELETE',
        headers: authHeaders(user.token),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        show('err', typeof json.error === 'string' ? json.error : 'Delete failed.')
      } else {
        show('ok', 'Program deleted.')
        if (editingId === p._id) cancelEdit()
        void loadPrograms()
      }
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'border-zinc-600 bg-zinc-950 text-zinc-100 placeholder:text-zinc-500 focus-visible:ring-emerald-500/40'

  return (
    <div className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">
      <h1 className="mb-2 text-2xl font-semibold tracking-tight text-zinc-100">Admin — grow programs</h1>
      <p className="mb-8 max-w-2xl text-sm text-zinc-400">
        Create and edit preset programs (temperature, humidity, EC, pH, lights on). Mutations require your account
        email to be listed in the backend <code className="text-zinc-300">ADMIN_EMAILS</code> env variable.
      </p>

      {message ? (
        <div
          className={cn(
            'mb-6 rounded-md border px-4 py-3 text-sm',
            message.type === 'ok'
              ? 'border-emerald-800 bg-emerald-950/50 text-emerald-200'
              : 'border-red-900 bg-red-950/40 text-red-200'
          )}
          role="status"
        >
          {message.text}
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[1fr,1.2fr]">
        <Card className="border-zinc-700 bg-zinc-900/80 text-zinc-100 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? 'Edit program' : 'New program'}</CardTitle>
            <CardDescription className="text-zinc-400">
              {editingId ? 'Update fields and save, or cancel to clear.' : 'Add a preset users can pick for a system.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor="ap-name">Name</Label>
                <Input
                  id="ap-name"
                  className={inputClass}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  autoComplete="off"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ap-temp">Temperature</Label>
                  <Input
                    id="ap-temp"
                    type="number"
                    step="any"
                    className={inputClass}
                    value={form.temperature}
                    onChange={(e) => setForm((f) => ({ ...f, temperature: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ap-hum">Humidity %</Label>
                  <Input
                    id="ap-hum"
                    type="number"
                    step="any"
                    className={inputClass}
                    value={form.humidity}
                    onChange={(e) => setForm((f) => ({ ...f, humidity: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ap-ec">EC</Label>
                  <Input
                    id="ap-ec"
                    type="number"
                    step="any"
                    className={inputClass}
                    value={form.ec}
                    onChange={(e) => setForm((f) => ({ ...f, ec: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ap-ph">pH</Label>
                  <Input
                    id="ap-ph"
                    type="number"
                    step="any"
                    className={inputClass}
                    value={form.ph}
                    onChange={(e) => setForm((f) => ({ ...f, ph: e.target.value }))}
                  />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="ap-lights">Lights on (hours)</Label>
                  <Input
                    id="ap-lights"
                    type="number"
                    step="any"
                    className={inputClass}
                    value={form.lights_on}
                    onChange={(e) => setForm((f) => ({ ...f, lights_on: e.target.value }))}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-500">
                  {editingId ? 'Save changes' : 'Create program'}
                </Button>
                {editingId ? (
                  <Button type="button" variant="outline" className="border-zinc-600 text-zinc-200" onClick={cancelEdit}>
                    Cancel
                  </Button>
                ) : null}
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="border-zinc-700 bg-zinc-900/80 text-zinc-100 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg">Existing programs</CardTitle>
            <CardDescription className="text-zinc-400">{programs.length} preset(s)</CardDescription>
          </CardHeader>
          <CardContent className="max-h-[min(70vh,520px)] overflow-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-zinc-700 text-zinc-400">
                  <th className="py-2 pr-3 font-medium">Name</th>
                  <th className="py-2 pr-2 font-medium">T</th>
                  <th className="py-2 pr-2 font-medium">RH</th>
                  <th className="py-2 pr-2 font-medium">EC</th>
                  <th className="py-2 pr-2 font-medium">pH</th>
                  <th className="py-2 pr-2 font-medium">Light</th>
                  <th className="py-2 pl-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((p) => (
                  <tr key={p._id} className="border-b border-zinc-800/80">
                    <td className="py-2 pr-3 text-zinc-100">{p.name}</td>
                    <td className="py-2 pr-2 text-zinc-300">{p.temperature}</td>
                    <td className="py-2 pr-2 text-zinc-300">{p.humidity}</td>
                    <td className="py-2 pr-2 text-zinc-300">{p.ec}</td>
                    <td className="py-2 pr-2 text-zinc-300">{p.ph}</td>
                    <td className="py-2 pr-2 text-zinc-300">{p.lights_on}</td>
                    <td className="py-2 pl-2 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-emerald-400 hover:bg-zinc-800 hover:text-emerald-300"
                        onClick={() => startEdit(p)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:bg-zinc-800 hover:text-red-300"
                        onClick={() => void handleDelete(p)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {programs.length === 0 ? <p className="py-6 text-center text-zinc-500">No programs yet.</p> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AdminPrograms
